import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Stethoscope, Pill, Receipt, BedDouble, MessageSquare, RefreshCw, Calendar as CalendarIcon, FileSpreadsheet, Zap } from "lucide-react";
import * as XLSX from "xlsx";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);

export default function ManagerReports() {
  const { branch } = useAuth();
  const [date, setDate] = useState(today());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/end-of-day", { params: { branch_code: branch.code, date } });
      setReport(data);
    } catch (err) { toast.error(formatApiError(err, "Load failed")); }
    finally { setLoading(false); }
  }, [branch?.code, date]);

  useEffect(() => { load(); }, [load]);

  const exportExcel = () => {
    if (!report) return;
    const directRows = (report.direct_bills || []).map(b => ({
      "Bill No": b.bill_no,
      "Customer": b.customer_name,
      "Phone": b.customer_phone || "",
      "Items": (b.items || []).map(i => `${i.name} × ${i.qty}`).join("; "),
      "Total (₹)": Number(b.total || 0),
      "Notes": b.notes || "",
      "Cashier": b.generated_by_name || b.generated_by || "",
      "Generated At": b.generated_at || "",
    }));
    const patientRows = (report.patient_bills || []).map(b => ({
      "MRN": b.mrn,
      "OP Token": b.op_token,
      "Patient": b.patient_name,
      "Phone": b.phone || "",
      "Doctor": b.doctor,
      "Pharmacy Token": b.pharm_token || "",
      "Total (₹)": Number(b.total || 0),
      "Cashier": b.generated_by || "",
      "Generated At": b.generated_at || "",
    }));
    const summary = [
      { Metric: "Date", Value: report.date },
      { Metric: "Branch", Value: `${branch.name} (${branch.code})` },
      { Metric: "Patients today", Value: report.patients_today },
      { Metric: "Consultations", Value: report.consultations_done },
      { Metric: "Pharmacy dispensed", Value: report.pharmacy_dispensed },
      { Metric: "Consultation bills", Value: report.bills_generated },
      { Metric: "Consultation revenue (₹)", Value: report.revenue },
      { Metric: "Direct bills", Value: report.direct_bills_count || 0 },
      { Metric: "Direct bills revenue (₹)", Value: report.direct_bills_revenue || 0 },
      { Metric: "Total revenue (₹)", Value: Number(report.revenue || 0) + Number(report.direct_bills_revenue || 0) },
      { Metric: "Active admissions", Value: report.active_admissions },
      { Metric: "SMS sent", Value: report.sms_sent },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), "Summary");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(directRows.length ? directRows : [{ "Bill No": "—", "Customer": "No direct bills on this date" }]), "Direct Bills");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(patientRows.length ? patientRows : [{ "MRN": "—", "Patient": "No patient bills on this date" }]), "Patient Bills");
    const fname = `EOD_${branch.code}_${report.date}.xlsx`;
    XLSX.writeFile(wb, fname);
    toast.success(`Downloaded ${fname}`);
  };

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
            Manager · End-of-day · {branch.name}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1">End-of-day report</h1>
          <p className="text-[#5C5652]">One-tap daily rollup — patients, consults, bills, revenue, IP census. Includes walk-in direct bills.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <CalendarIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5652]" strokeWidth={1.5} />
            <input data-testid="report-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="h-10 pl-10 pr-3 rounded-lg border border-sand bg-white focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          </div>
          <button onClick={load} data-testid="report-refresh" className="h-10 px-4 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] text-sm font-semibold inline-flex items-center gap-2">
            <RefreshCw className="h-4 w-4" strokeWidth={1.75} /> Refresh
          </button>
          <button onClick={exportExcel} disabled={!report} data-testid="report-export-excel" className="h-10 px-4 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            <FileSpreadsheet className="h-4 w-4" strokeWidth={1.75} /> Export Excel
          </button>
          <button onClick={() => window.print()} data-testid="report-print" className="h-10 px-4 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover">Print</button>
        </div>
      </div>

      {loading || !report ? <div className="p-8 bg-white border border-sand rounded-lg text-sm text-[#5C5652]">Loading…</div> : (
        <>
          <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 mb-8" data-testid="report-kpis">
            <Kpi label="Patients today" value={report.patients_today} icon={Users} />
            <Kpi label="Consultations" value={report.consultations_done} icon={Stethoscope} />
            <Kpi label="Pharmacy dispensed" value={report.pharmacy_dispensed} icon={Pill} />
            <Kpi label="Consultation bills" value={report.bills_generated} icon={Receipt} />
            <Kpi label="Direct bills" value={report.direct_bills_count || 0} icon={Zap} />
            <Kpi label="Total revenue" value={`₹ ${(Number(report.revenue) + Number(report.direct_bills_revenue || 0)).toFixed(2)}`} icon={Receipt} big />
            <Kpi label="Active admissions" value={report.active_admissions} icon={BedDouble} />
            <Kpi label="SMS sent" value={report.sms_sent} icon={MessageSquare} />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="bg-white border border-sand rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-sand text-xs font-bold uppercase tracking-wider text-[#5C5652]">Top doctors by patients billed</div>
              {report.top_doctors.length === 0 ? (
                <div className="p-6 text-sm text-[#5C5652]">No consultation bills on this date.</div>
              ) : (
                <ul className="divide-y divide-[#E8E3D9]" data-testid="top-doctors-list">
                  {report.top_doctors.map((d, i) => (
                    <li key={d.name + i} className="px-6 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-[#1A4331]/10 text-forest flex items-center justify-center font-bold text-sm">{i + 1}</div>
                        <div className="font-semibold text-[#2A2624]">{d.name}</div>
                      </div>
                      <div className="font-display text-lg text-forest">{d.count}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white border border-sand rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-sand text-xs font-bold uppercase tracking-wider text-[#5C5652] flex items-center justify-between">
                <span>Direct bills</span>
                <span className="font-mono text-forest">₹ {Number(report.direct_bills_revenue || 0).toFixed(2)}</span>
              </div>
              {(report.direct_bills || []).length === 0 ? (
                <div className="p-6 text-sm text-[#5C5652]">No walk-in bills on this date.</div>
              ) : (
                <ul className="divide-y divide-[#E8E3D9] max-h-80 overflow-y-auto" data-testid="direct-bills-list">
                  {report.direct_bills.map((b) => (
                    <li key={b.id} className="px-6 py-3 flex items-center justify-between gap-3" data-testid={`report-direct-${b.bill_no}`}>
                      <div className="min-w-0">
                        <div className="font-mono text-xs text-forest">{b.bill_no}</div>
                        <div className="font-semibold text-[#2A2624] truncate">{b.customer_name}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-forest">₹ {Number(b.total).toFixed(2)}</div>
                        <div className="text-[10px] text-[#5C5652]">{(b.items || []).length} item{(b.items || []).length === 1 ? "" : "s"}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, big }) {
  return (
    <div className={`p-5 rounded-lg bg-white border border-sand ${big ? "col-span-2 lg:col-span-2" : ""}`}>
      <div className="flex items-center gap-2 text-[#5C5652] text-[10px] font-bold uppercase tracking-[0.22em] mb-2">
        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} /> {label}
      </div>
      <div className={`font-display text-forest leading-none ${big ? "text-4xl" : "text-3xl"}`}>{value}</div>
    </div>
  );
}
